import React, { useState, useEffect, useRef, useImperativeHandle, forwardRef, useCallback } from 'react';
import { Button, Input, Space, Tag, Modal, Image, Spin, Select, Dropdown } from 'antd';
import { SearchOutlined, FilterOutlined, DownloadOutlined, MoreOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import type { TableProps } from 'antd';
import CommonTable from 'components/CommonTable';
import dayjs from 'utils/dayjs';
import { useDownloadFile } from 'hooks/useDownloadFile';
import { useDownloadImage } from 'hooks/useDownloadImage';
import { EDocumentSortBy, EDocumentSortType, IGetDocumentItem, IGetDocumentListParams } from 'types/document';
import { EMeetingUserType } from 'types/zoom';
import useGetDocument from 'hooks/useGetDocument';
import useGetDocumentFilters from 'hooks/useGetDocumentFilters';
import EditDocumentModal from '../EditDocumentModal';
import { getProviderPrivateFileSigned } from 'api/common';
import { markDocumentAsSeen } from 'api/documentApi';
import EmptyBg from '../../imgs/empty-bg.png';
import s from './s.module.less';

interface DocumentsTableProps {
    clientId?: number;
    onUpload?: () => void;
    onHasDocumentsChange?: (hasDocuments: boolean) => void;
    showUploadButton?: boolean;
    selectable?: boolean;
    /** When true with selectable, list includes patient and provider uploads (default: provider-only when selectable). */
    includeAllDocuments?: boolean;
    /** When true, only PDF documents are displayed (client-side filter). */
    pdfOnly?: boolean;
    /** Initial page size for the document list (default 10). */
    listPageSize?: number;
    /** Use radio for single selection (e.g. fax one PDF). */
    selectionType?: 'checkbox' | 'radio';
    onSelectRows?: (selectedRows: IGetDocumentItem[]) => void;
    hideEdit?: boolean;
    /** Row menu — Send fax (PDF only); same POST /fax/send as eFax dashboard with source=DOCUMENT. */
    onSendFax?: (record: IGetDocumentItem) => void;
    isGroupPractice?: boolean;
}

const DocumentsTable = forwardRef<{ refreshData: () => void }, DocumentsTableProps>(
    (
        {
            clientId,
            onUpload,
            onHasDocumentsChange,
            showUploadButton = true,
            selectable = false,
            includeAllDocuments = false,
            pdfOnly = false,
            listPageSize = 10,
            selectionType = 'checkbox',
            onSelectRows,
            hideEdit = false,
            onSendFax,
            isGroupPractice = false,
        },
        ref,
    ) => {
        const [pendingNameFilter, setPendingNameFilter] = useState('');
        const [pendingVisibleToPatientFilter, setPendingVisibleToPatientFilter] = useState<boolean | undefined>(
            undefined,
        );
        const [pendingUploadByFilter, setPendingUploadByFilter] = useState<boolean | undefined>(undefined);
        const [pendingTagsFilter, setPendingTagsFilter] = useState<string[]>([]);
        const [imageModalVisible, setImageModalVisible] = useState(false);
        const [currentImage, setCurrentImage] = useState<IGetDocumentItem>();
        const [previewUrl, setPreviewUrl] = useState<string>();
        const [showEditModal, setShowEditModal] = useState(false);
        const [editingDocument, setEditingDocument] = useState<IGetDocumentItem>();
        const [documentsFromHook, hasDocuments, loading, totalRecords, fetchDocuments] = useGetDocument(clientId, false, isGroupPractice);
        const [documents, setDocuments] = useState<IGetDocumentItem[] | undefined>(documentsFromHook);
        const [tags, uploadedByOptions, visibleToOptions, fetchDocumentFilters] = useGetDocumentFilters(isGroupPractice, clientId);
        const [filters, setFilters] = useState<IGetDocumentListParams>({
            clientId,
            current: 1,
            size: listPageSize,
            sortBy: EDocumentSortBy.UPLOAD_TIME,
            sortType: EDocumentSortType.DESC,
            tags: [],
            uploadBySelf: selectable && !includeAllDocuments ? true : undefined,
        });

        // 同步 hook 返回的 documents 到本地状态
        useEffect(() => {
            setDocuments(documentsFromHook);
        }, [documentsFromHook]);

        // 使用 useRef 跟踪上一次的 filters 值，避免无限循环
        const prevFiltersRef = useRef<string>('');

        // 当 filters 或 clientId 变化时重新请求
        useEffect(() => {
            const currentFilters: IGetDocumentListParams = {
                ...filters,
                clientId,
            };

            // 将 filters 序列化为字符串进行比较
            const filtersKey = JSON.stringify({
                clientId: currentFilters.clientId,
                current: currentFilters.current,
                size: currentFilters.size,
                sortBy: currentFilters.sortBy,
                sortType: currentFilters.sortType,
                tags: currentFilters.tags,
                name: currentFilters.name,
                visibleToPatient: currentFilters.visibleToPatient,
                uploadBySelf: currentFilters.uploadBySelf,
            });

            // 只有当 filters 真正变化时才请求
            if (prevFiltersRef.current !== filtersKey) {
                prevFiltersRef.current = filtersKey;
                fetchDocuments(currentFilters);
            }
        }, [filters, clientId]);

        // 同步 filters 和 pending 状态
        useEffect(() => {
            setPendingVisibleToPatientFilter(filters.visibleToPatient);
            setPendingUploadByFilter(filters.uploadBySelf);
            setPendingNameFilter(filters.name || '');
            setPendingTagsFilter(filters.tags || []);
        }, [filters.visibleToPatient, filters.uploadBySelf, filters.name, filters.tags]);

        // 通知父组件 hasDocuments 的变化
        useEffect(() => {
            onHasDocumentsChange?.(hasDocuments);
        }, [hasDocuments, onHasDocumentsChange]);

        // 暴露 refreshData 方法给父组件
        const refreshData = useCallback(() => {
            const currentFilters: IGetDocumentListParams = {
                ...filters,
                clientId,
            };
            fetchDocuments(currentFilters);
            fetchDocumentFilters();
        }, [filters, clientId, fetchDocuments, fetchDocumentFilters]);

        useImperativeHandle(
            ref,
            () => ({
                refreshData,
            }),
            [refreshData],
        );

        const downloadImage = useDownloadImage();
        const downloadFile = useDownloadFile();

        // 判断文件类型
        const getFileType = (documentUrl: string, documentType: string): 'image' | 'pdf' | 'other' => {
            const url = documentUrl.toLowerCase();
            if (
                url.includes('.jpg') ||
                url.includes('.jpeg') ||
                url.includes('.png') ||
                url.includes('.gif') ||
                url.includes('.webp') ||
                documentType?.toLowerCase().includes('image')
            ) {
                return 'image';
            }
            if (url.includes('.pdf') || documentType?.toLowerCase().includes('pdf')) {
                return 'pdf';
            }
            return 'other';
        };

        const displayDocuments = React.useMemo(() => {
            if (!pdfOnly) return documents || [];
            return (documents || []).filter((d) => getFileType(d.documentUrl, d.documentType) === 'pdf');
        }, [documents, pdfOnly]);

        const hasDisplayDocuments = displayDocuments.length > 0;

        const getPreviewUrl = async (v?: string) => {
            if (v) {
                try {
                    const res = await getProviderPrivateFileSigned(v);
                    if (!res.error && res.data?.data?.singedUrl) {
                        setPreviewUrl(res.data.data.singedUrl);
                        return res.data.data.singedUrl;
                    }
                } catch (e) {
                    console.error(e);
                }
            }
        };

        const handleDownload = async (item: IGetDocumentItem) => {
            const previewUrl = await getPreviewUrl(item.documentUrl);
            if (!previewUrl) return;
            const fileType = getFileType(item.documentUrl, item.documentType);
            if (fileType === 'image') {
                downloadImage(previewUrl, item.name);
            } else {
                downloadFile(previewUrl, item.name);
            }
        };

        const handlePreview = async (item: IGetDocumentItem) => {
            // 如果 newFlag 为 true，调用 markDocumentAsSeen 接口
            if (item.newFlag && item.newVersion) {
                try {
                    await markDocumentAsSeen(item.id.toString(), item.newVersion.toString());
                    // 更新本地 documents，隐藏红点
                    setDocuments((prev) => {
                        if (!prev) return prev;
                        return prev.map((doc) => (doc.id === item.id ? { ...doc, newFlag: false } : doc));
                    });
                } catch (e) {
                    console.error('Failed to mark document as seen:', e);
                }
            }

            const previewUrl = await getPreviewUrl(item.documentUrl);
            if (!previewUrl) return;
            const fileType = getFileType(item.documentUrl, item.documentType);
            if (fileType === 'image') {
                setImageModalVisible(true);
                setCurrentImage(item);
            } else {
                window.open(previewUrl, '_blank');
            }
        };

        const handleEdit = (document: IGetDocumentItem) => {
            setEditingDocument(document);
            setShowEditModal(true);
        };

        // 处理表格变化（分页、排序、过滤）
        const handleTableChange: TableProps<IGetDocumentItem>['onChange'] = (pagination) => {
            if (pagination?.current) {
                setFilters((prev) => ({
                    ...prev,
                    current: pagination.current || 1,
                }));
            }
        };

        // 判断上传者
        const getUploadedBy = (item: IGetDocumentItem): 'You' | 'Patient' => {
            return item.createdByType === EMeetingUserType.PROVIDER ? 'You' : 'Patient';
        };

        const columns: ColumnsType<IGetDocumentItem> = [
            {
                title: 'NAME',
                dataIndex: 'name',
                key: 'name',
                width: 300,
                filterDropdown: () => (
                    <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                        <Input
                            value={pendingNameFilter}
                            onChange={(e) => setPendingNameFilter(e.target.value)}
                            onPressEnter={() => {
                                setFilters((prev) => ({
                                    ...prev,
                                    name: pendingNameFilter.trim() || undefined,
                                    current: 1,
                                }));
                            }}
                            placeholder="Search name"
                            style={{ marginBottom: 8, display: 'block' }}
                        />
                        <Space>
                            <Button
                                type="primary"
                                onClick={() => {
                                    setFilters((prev) => ({
                                        ...prev,
                                        name: pendingNameFilter.trim() || undefined,
                                        current: 1,
                                    }));
                                }}
                                icon={<SearchOutlined />}
                                size="small"
                                style={{ width: 90 }}
                            >
                                Search
                            </Button>
                            <Button
                                onClick={() => {
                                    setPendingNameFilter('');
                                    setFilters((prev) => ({
                                        ...prev,
                                        name: undefined,
                                        current: 1,
                                    }));
                                }}
                                size="small"
                                style={{ width: 90 }}
                            >
                                Reset
                            </Button>
                        </Space>
                    </div>
                ),
                filterIcon: (filtered: boolean) => (
                    <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
                ),
                filteredValue: filters.name ? [filters.name] : null,
                render: (name: string, record: IGetDocumentItem) => (
                    <div className={s.nameCell}>
                        {record.newFlag && <span className={s.newDot} />}
                        <span className={s.nameText}>{name}</span>
                    </div>
                ),
            },
            ...(isGroupPractice
                ? [
                      {
                          title: 'PROVIDER',
                          key: 'provider',
                          width: 200,
                          render: (_: any, record: IGetDocumentItem) => {
                              const name = record.provider
                                  ? `${record.provider.firstName || ''} ${record.provider.lastName || ''}`.trim()
                                  : '-';
                              return name;
                          },
                      },
                  ]
                : []),
            {
                title: 'TAG',
                dataIndex: 'tag',
                key: 'tag',
                width: 180,
                filterDropdown: () => (
                    <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                        <Select
                            mode="multiple"
                            value={pendingTagsFilter}
                            onChange={(value) => {
                                setPendingTagsFilter(value);
                            }}
                            style={{ width: '100%', marginBottom: 8 }}
                            placeholder="Select tags"
                            maxTagCount="responsive"
                        >
                            {tags.map((tag) => (
                                <Select.Option key={tag} value={tag}>
                                    {tag}
                                </Select.Option>
                            ))}
                        </Select>
                        <Space>
                            <Button
                                type="primary"
                                onClick={() => {
                                    setFilters((prev) => ({
                                        ...prev,
                                        tags: pendingTagsFilter.length > 0 ? pendingTagsFilter : undefined,
                                        current: 1,
                                    }));
                                }}
                                icon={<FilterOutlined />}
                                size="small"
                                style={{ width: 90 }}
                            >
                                Filter
                            </Button>
                            <Button
                                onClick={() => {
                                    setPendingTagsFilter([]);
                                    setFilters((prev) => ({
                                        ...prev,
                                        tags: undefined,
                                        current: 1,
                                    }));
                                }}
                                size="small"
                                style={{ width: 90 }}
                            >
                                Reset
                            </Button>
                        </Space>
                    </div>
                ),
                filterIcon: (filtered: boolean) => (
                    <FilterOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
                ),
                filteredValue: filters.tags && filters.tags.length > 0 ? filters.tags : null,
                render: (tags: string[]) => (
                    <Space size={[0, 8]} wrap>
                        {tags && tags.length > 0 ? (
                            tags.map((tag, index) => (
                                <Tag key={index} className={s.tag}>
                                    {tag}
                                </Tag>
                            ))
                        ) : (
                            <span>-</span>
                        )}
                    </Space>
                ),
            },
            {
                title: 'VISIBLE TO PATIENT',
                dataIndex: 'visibleToPatient',
                key: 'visibleToPatient',
                width: 160,
                filterDropdown: () => (
                    <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                        <Select
                            value={
                                pendingVisibleToPatientFilter === undefined
                                    ? 'All'
                                    : pendingVisibleToPatientFilter
                                      ? 'Yes'
                                      : 'No'
                            }
                            onChange={(value) => {
                                const filterValue = value === 'All' ? undefined : value === 'Yes';
                                setPendingVisibleToPatientFilter(filterValue);
                            }}
                            style={{ width: '100%', marginBottom: 8 }}
                            placeholder="Select filter"
                        >
                            <Select.Option value="All">All</Select.Option>
                            <Select.Option value="Yes">Yes</Select.Option>
                            <Select.Option value="No">No</Select.Option>
                        </Select>
                        <Space>
                            <Button
                                type="primary"
                                onClick={() => {
                                    setFilters((prev) => ({
                                        ...prev,
                                        visibleToPatient: pendingVisibleToPatientFilter,
                                        current: 1,
                                    }));
                                }}
                                icon={<FilterOutlined />}
                                size="small"
                                style={{ width: 90 }}
                            >
                                Filter
                            </Button>
                            <Button
                                onClick={() => {
                                    setPendingVisibleToPatientFilter(undefined);
                                    setFilters((prev) => ({
                                        ...prev,
                                        visibleToPatient: undefined,
                                        current: 1,
                                    }));
                                }}
                                size="small"
                                style={{ width: 90 }}
                            >
                                Reset
                            </Button>
                        </Space>
                    </div>
                ),
                filterIcon: (filtered: boolean) => (
                    <FilterOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
                ),
                filteredValue:
                    filters.visibleToPatient !== undefined ? [filters.visibleToPatient ? 'Yes' : 'No'] : null,
                render: (visible: boolean) => (visible ? 'Yes' : 'No'),
            },
            {
                title: 'UPLOADED BY',
                key: 'uploadedBy',
                width: 140,
                ...(selectable
                    ? {}
                    : {
                          filterDropdown: () => (
                              <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                                  <Select
                                      value={
                                          pendingUploadByFilter === undefined
                                              ? 'All'
                                              : pendingUploadByFilter
                                                ? 'You'
                                                : 'Patient'
                                      }
                                      onChange={(value) => {
                                          const filterValue = value === 'All' ? undefined : value === 'You';
                                          setPendingUploadByFilter(filterValue);
                                      }}
                                      style={{ width: '100%', marginBottom: 8 }}
                                      placeholder="Select filter"
                                  >
                                      <Select.Option value="All">All</Select.Option>
                                      <Select.Option value="You">You</Select.Option>
                                      <Select.Option value="Patient">Patient</Select.Option>
                                  </Select>
                                  <Space>
                                      <Button
                                          type="primary"
                                          onClick={() => {
                                              setFilters((prev) => ({
                                                  ...prev,
                                                  uploadBySelf: pendingUploadByFilter,
                                                  current: 1,
                                              }));
                                          }}
                                          icon={<FilterOutlined />}
                                          size="small"
                                          style={{ width: 90 }}
                                      >
                                          Filter
                                      </Button>
                                      <Button
                                          onClick={() => {
                                              setPendingUploadByFilter(undefined);
                                              setFilters((prev) => ({
                                                  ...prev,
                                                  uploadBySelf: undefined,
                                                  current: 1,
                                              }));
                                          }}
                                          size="small"
                                          style={{ width: 90 }}
                                      >
                                          Reset
                                      </Button>
                                  </Space>
                              </div>
                          ),
                          filterIcon: (filtered: boolean) => (
                              <FilterOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
                          ),
                          filteredValue:
                              filters.uploadBySelf !== undefined ? [filters.uploadBySelf ? 'You' : 'Patient'] : null,
                      }),
                render: (_, record) => getUploadedBy(record),
            },
            {
                title: 'NOTES',
                dataIndex: 'note',
                key: 'note',
                width: 200,
                render: (note: string) => note || '-',
            },
            {
                title: 'DATE UPLOADED',
                dataIndex: 'createdAt',
                key: 'createdAt',
                width: 150,
                sorter: (a, b) => dayjs(a.createdAt).unix() - dayjs(b.createdAt).unix(),
                render: (date: string) => dayjs(date).format('MM/DD/YYYY'),
            },
            {
                title: 'LAST MODIFIED',
                dataIndex: 'updatedAt',
                key: 'updatedAt',
                width: 150,
                sorter: (a, b) => dayjs(a.updatedAt).unix() - dayjs(b.updatedAt).unix(),
                render: (date: string) => dayjs(date).format('MM/DD/YYYY'),
            },
            {
                title: '',
                key: 'actions',
                width: 200,
                fixed: 'right',
                render: (_, record) => {
                    const fileKind = getFileType(record.documentUrl, record.documentType);
                    const showFaxMenu = Boolean(onSendFax) && fileKind === 'pdf';
                    return (
                        <Space align="center">
                            {!hideEdit && (
                                <Button type="link" className={s.actionLink} onClick={() => handleEdit(record)}>
                                    Edit
                                </Button>
                            )}
                            <Button
                                type="link"
                                className={s.actionLink}
                                onClick={() => {
                                    handlePreview(record);
                                }}
                            >
                                Preview
                            </Button>
                            <Button
                                type="link"
                                className={s.actionLink}
                                onClick={() => {
                                    handleDownload(record);
                                }}
                            >
                                Download
                            </Button>
                            {showFaxMenu && (
                                <Dropdown
                                    menu={{
                                        items: [
                                            {
                                                key: 'send-fax',
                                                label: 'Send fax',
                                                onClick: () => onSendFax?.(record),
                                            },
                                        ],
                                    }}
                                    trigger={['click']}
                                >
                                    <button type="button" className={s.actionMenuTrigger} aria-label="More actions">
                                        <MoreOutlined />
                                    </button>
                                </Dropdown>
                            )}
                        </Space>
                    );
                },
            },
        ];

        const EmptyState = () => (
            <div className={s.emptyState}>
                <div className={s.emptyIllustration}>
                    <Image src={EmptyBg} alt="Empty" width={264} height={209} preview={false} />
                </div>
                <div className={s.emptyTitle}>No documents yet</div>
                <div className={s.emptyDescription}>Upload documents and share with patients today</div>
                {showUploadButton && (
                    <Button type="primary" onClick={onUpload} className={s.emptyButton}>
                        Upload document
                    </Button>
                )}
            </div>
        );

        const rowSelection: TableProps<IGetDocumentItem>['rowSelection'] = {
            type: selectionType,
            onChange: (_selectedRowKeys: React.Key[], selectedRows: IGetDocumentItem[]) => {
                if (pdfOnly) {
                    onSelectRows?.(selectedRows.filter((d) => getFileType(d.documentUrl, d.documentType) === 'pdf'));
                    return;
                }
                onSelectRows?.(selectedRows);
            },
        };

        return (
            <>
                <Spin spinning={loading}>
                    {hasDisplayDocuments ? (
                        <CommonTable<IGetDocumentItem>
                            columns={columns}
                            data={displayDocuments}
                            rowKey="id"
                            pagination={
                                pdfOnly
                                    ? false
                                    : {
                                          current: filters.current || 1,
                                          pageSize: filters.size || 10,
                                          total: totalRecords,
                                          showSizeChanger: false,
                                      }
                            }
                            onChange={handleTableChange}
                            bordered={false}
                            scroll={{ x: 'max-content' }}
                            loading={loading}
                            rowSelection={selectable ? rowSelection : undefined}
                        />
                    ) : (
                        <EmptyState />
                    )}
                </Spin>
                <Modal
                    open={imageModalVisible}
                    onCancel={() => setImageModalVisible(false)}
                    footer={null}
                    width="80%"
                    centered
                    closeIcon={null}
                    title={
                        <div className={s.imageModalHeader}>
                            <div
                                className={s.imageModalActions}
                                onClick={() => currentImage && handleDownload(currentImage)}
                            >
                                <DownloadOutlined />
                            </div>
                        </div>
                    }
                >
                    <div className={s.imageContainer}>
                        <img src={previewUrl || ''} alt="Document Preview" className={s.previewImage} />
                    </div>
                </Modal>
                <EditDocumentModal
                    open={showEditModal}
                    onClose={() => {
                        setShowEditModal(false);
                        setEditingDocument(undefined);
                    }}
                    onSuccess={() => {
                        setShowEditModal(false);
                        setEditingDocument(undefined);
                        refreshData();
                    }}
                    initialData={editingDocument}
                    clientId={clientId!}
                    isGroupPractice={isGroupPractice}
                />
            </>
        );
    },
);

export default DocumentsTable;
